import { HttpBackend, HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { retryBackoff } from 'backoff-rxjs';
import { EMPTY, Observable, from, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, shareReplay, take, tap, toArray } from 'rxjs/operators';

/**
 * A progress/completion event from {@link ImageLoaderService.loadImageToBlob}.
 */
export interface IImageBlobEvent {
  /** Download progress 0–100. */
  progress: number;
  /** The downloaded image, present on the completion event. */
  blob?: Blob | null;
  /**
   * True when the blob was served synchronously from the session cache —
   * consumers can skip the load affordance (fade, spinner) for an image that
   * is already on screen-ready.
   */
  fromCache?: boolean;
}

/**
 * Loads images. Two families:
 *
 * - `loadImageToCache` / `loadImagesToCache` — element-based pre-warmers
 *   (offscreen `Image` elements) used by screens that want assets ready before
 *   they show (shell background, team picker, dynamic path). The singular form
 *   rejects on failure; the batch form skips failed images. Note the
 *   element-based pre-warm won't work with the HTTP cache disabled.
 * - `loadImageToBlob` — the single-image pipeline behind `app-image`:
 *   download with progress + retry, **memoized in a session blob cache** so
 *   every remount of the same `src` (tab switches, screen re-resolves)
 *   resolves synchronously instead of re-downloading and re-playing the
 *   load affordance. Concurrent requests for one `src` share a single download.
 *
 * The cache holds `Blob`s, not object URLs — consumers create and revoke
 * their own object URLs, so ownership stays exactly where it was and cache
 * eviction can never invalidate a URL in use. Entries are evicted oldest-
 * first once the byte budget is exceeded (cache keys are URLs, so servers
 * cache-bust the same way they already must for the browser's HTTP cache).
 */
@Injectable({
  providedIn: 'root',
})
export class ImageLoaderService {
  // HttpBackend (not the default HttpClient) deliberately bypasses the
  // interceptor chain — image bytes shouldn't flow through API interceptors
  // (and, locally, shouldn't be swallowed by the mock interceptor).
  private readonly http = new HttpClient(inject(HttpBackend));

  /** Session blob cache for {@link loadImageToBlob}, keyed by URL. */
  private readonly blobCache = new Map<string, Blob>();

  /** Total bytes currently held by {@link blobCache}. */
  private cacheBytes = 0;

  /** Oldest entries are evicted once the cache exceeds this many bytes. */
  private static readonly CACHE_BYTE_BUDGET = 50 * 1024 * 1024;

  /** In-flight downloads keyed by URL, so concurrent mounts share one request. */
  private readonly inFlight = new Map<string, Observable<IImageBlobEvent>>();

  /**
   * Pre-warm the browser's HTTP cache for a single image by loading it through
   * an offscreen `Image` element. **Rejects** (after retries) when the image
   * can't be loaded — callers that want a whole batch to survive individual
   * failures should use {@link loadImagesToCache}, which skips failed images.
   *
   * @param url - the image URL to pre-warm
   * @returns the loaded image `src`, as a single emission
   */
  loadImageToCache(url: string): Observable<string> {
    if (!url) {
      return throwError(
        () => new Error('[Image Loader Service] : loadImageToCache - You must provide a valid image URL when using the image loader.'),
      );
    }

    return new Observable<string>((observer) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        observer.next(img.src);
        observer.complete();
      };
      img.onerror = (err) => {
        observer.error(err);
      };
    }).pipe(
      retryBackoff({
        initialInterval: 500,
        maxRetries: 3,
        maxInterval: 5000,
      }),
    );
  }

  /**
   * Pre-warm the browser's HTTP cache for a batch of images (each via
   * {@link loadImageToCache}). Downloads run concurrently, so the single
   * emitted array is ordered by completion, **not** by input order.
   *
   * Individual images that fail after their retries are **skipped** — one bad
   * URL never rejects the batch, and the emitted array holds only the `src`
   * values that loaded. Rejects only if `urls` is empty or holds an empty
   * string.
   *
   * @param urls - the image URLs to pre-warm
   * @returns the successfully-loaded image `src` values, as a single emission
   */
  loadImagesToCache(urls: string[]): Observable<string[]> {
    if (!urls || !urls.length) {
      return throwError(() => new Error('[Image Loader Service] - You must provide an array of images when using the image loader.'));
    }

    if (urls.some((url) => url.length === 0)) {
      return throwError(() => new Error('[Image Loader Service] - The loader must be presented with valid URLs to load.'));
    }

    return from(urls).pipe(
      mergeMap((url) => this.loadImageToCache(url).pipe(catchError(() => EMPTY))),
      toArray(),
      take(1),
    );
  }

  /**
   * Loads an image as a blob, with download progress and retry.
   *
   * Memoized: a URL already in the session cache resolves **synchronously**
   * (one `{progress: 100, blob, fromCache: true}` event), and concurrent
   * requests for the same URL share a single download. Failed downloads are
   * never cached — the next subscriber retries from scratch.
   *
   * @param url - The URL of the image to load.
   */
  loadImageToBlob(url: string): Observable<IImageBlobEvent> {
    // check the object exists
    if (!url) {
      return throwError(
        () => new Error('[Image Loader Service] : loadImageToBlob - You must provide a valid image URL when using the image loader.'),
      );
    }

    // check the URL provided is valid
    if (url.length === 0) {
      return throwError(() => new Error('[Image Loader Service] : loadImageToBlob - The provided URL is invalid.'));
    }

    // session cache hit — synchronous, so consumers can render with no load
    // affordance at all (no fade, no spinner, no ghost flash)
    const cached = this.blobCache.get(url);
    if (cached) {
      this.refreshCacheOrder(url, cached);
      return of({ progress: 100, blob: cached, fromCache: true });
    }

    // join an in-flight download of the same URL rather than starting another
    const existing = this.inFlight.get(url);
    if (existing) {
      return existing;
    }

    // load the image with progress
    const download = this.http
      .get(url, {
        responseType: 'blob',
        observe: 'events',
        reportProgress: true,
      })
      .pipe(
        retryBackoff({
          initialInterval: 500,
          maxRetries: 3,
          maxInterval: 5000,
        }),
        filter((event) => event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.Response),
        map((event): IImageBlobEvent => {
          if (event.type === HttpEventType.DownloadProgress) {
            const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
            return { progress };
          } else if (event.type === HttpEventType.Response) {
            return { progress: 100, blob: event.body };
          }
          return { progress: 0 };
        }),
        tap((event) => {
          if (event.blob) {
            this.storeInCache(url, event.blob);
          }
        }),
        catchError((error) => throwError(() => new Error(`[Image Loader Service] : loadImageToBlob - ${error.message}`))),
        // success, error, or every-subscriber-gone: this URL is no longer in
        // flight (completed downloads are answered by the cache from here on)
        finalize(() => this.inFlight.delete(url)),
        // one shared download; `refCount` cancels it if all consumers detach
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    this.inFlight.set(url, download);
    return download;
  }

  /**
   * Store a downloaded blob, evicting oldest entries (Map preserves insertion
   * order; cache hits re-insert) until the byte budget is respected. The entry
   * just stored is never evicted, so a single oversized image still serves its
   * current consumers — it just won't dislodge the rest of the cache.
   */
  private storeInCache(url: string, blob: Blob): void {
    const previous = this.blobCache.get(url);
    if (previous) {
      this.cacheBytes -= previous.size;
    }
    this.blobCache.set(url, blob);
    this.cacheBytes += blob.size;

    for (const [key, value] of this.blobCache) {
      if (this.cacheBytes <= ImageLoaderService.CACHE_BYTE_BUDGET) {
        break;
      }
      if (key === url) {
        continue;
      }
      this.blobCache.delete(key);
      this.cacheBytes -= value.size;
    }
  }

  /** Re-insert a hit entry so eviction order stays least-recently-used. */
  private refreshCacheOrder(url: string, blob: Blob): void {
    this.blobCache.delete(url);
    this.blobCache.set(url, blob);
  }
}
